import { Injectable } from '@angular/core';
import { PackagePlacementsRelation } from '@core/models/package-placements-relation';
import { PlacementSizesRelation } from '@core/models/placement-sizes-relation';
import { Size } from '@core/models/size';
import { PackageService } from '@core/services/package.service';
import { PlacementSize } from '@order/models/placement-size';
import { map, Observable, of } from 'rxjs';


@Injectable({
	providedIn: 'root'
})
export class ProductPlacementSizeComboService {

	private _isReady = false;

	private _validProductsPlacementsSizes = new Set<string>();
	private _validProductsPlacements = new Set<string>();
	private _validProductsSizes = new Set<string>();
	private _validPlacementsSizes = new Set<string>();

	// The placement sizes are stored per package per placement
	private _placementSizes = new Map<string, PlacementSize>();
	private _fullyQualifiedPlacementSizes = new Map<string, PlacementSize>();

	private _placements = new Map<string, PlacementSizesRelation>();
	private _fullyQualifiedPlacements = new Map<string, PlacementSizesRelation>();

	// The sizes are defined per product (package) per placement.
	// Validity should be impelemnted as Map<product-placement-key, Set<FixedSize>>
	// Currently all the sizes are kept in the _sizes collection

	private _sizesPerProductAndPlacement = new Map<string, Set<PlacementSize>>();


	constructor (private _packageService: PackageService) { }

	init(): Observable<void> {
		if (this._isReady) {
			return of(void (0)); // resolve immediately
		}

		const validCombos$: Observable<PackagePlacementsRelation[]> = this._packageService.getValidPackagesAndPlacements();
		return validCombos$.pipe(
			map((validCombos) => {
				this.internalInit(validCombos);
				this._isReady = true;
			})
		);
	}

	isValid(productId: string | undefined, placementId: string | undefined, sizeLabel: string | undefined): boolean {

		if (!this._isReady) {
			throw "The ProductPlacementSizeComboService is not initialized. Please call init() and subscribe for its Observable";
		}

		if (productId && placementId && sizeLabel) {
			const key = this.createProductPlacementSizeKey(productId, placementId, sizeLabel);
			return this._validProductsPlacementsSizes.has(key);
		}
		else if (productId && placementId) {
			const key = this.createProductPlacementKey(productId, placementId);
			return this._validProductsPlacements.has(key);
		}
		else if (placementId && sizeLabel) {
			const key = this.createPlacementSizeKey(placementId, sizeLabel);
			return this._validPlacementsSizes.has(key);
		}
		else if (productId && sizeLabel) {
			const key = this.createProductSizeKey(productId, sizeLabel);
			return this._validProductsSizes.has(key);
		}

		return true; // all params are empty or just one present
	}

	get placementSizesRelations(): Map<string, PlacementSizesRelation> {
		if (!this._isReady) {
			throw "The ProductPlacementSizeComboService is not initialized. Please call init() and subscribe for its Observable";
		}

		return this._placements;
	}

	getPlacementSizeRelation(packageCode: string, placementCode: string): PlacementSizesRelation | undefined {
		if (!this._isReady) {
			throw "The ProductPlacementSizeComboService is not initialized. Please call init() and subscribe for its Observable";
		}

		const key = this.createProductPlacementKey(packageCode, placementCode);
		return this._fullyQualifiedPlacements.get(key);
	}


	get allPlacementSizes(): PlacementSize[] {
		if (!this._isReady) {
			throw "The ProductPlacementSizeComboService is not initialized. Please call init() and subscribe for its Observable";
		}

		return Array.from(this._placementSizes.values());
	}

	getPlacementSize(packageCode: string, placementCode: string, sizeLabel: string): PlacementSize | undefined {
		const productPlacementSizeKey = this.createProductPlacementSizeKey(packageCode, placementCode, sizeLabel);
		return this._fullyQualifiedPlacementSizes.get(productPlacementSizeKey);
	}

	getSize(packageCode: string, placementCode: string, sizeLabel: string): Size | undefined {

		// The packageCode should be used later; when the sizes are stored per package per placement

		let result: Size | undefined = undefined;
		const sizes = this._sizesPerProductAndPlacement.get(this.createProductPlacementKey(packageCode, placementCode)) ?? [];
		for (let size of sizes) {
			if (size.label === sizeLabel) {
				result = size;
			}
		}
		return result;
	}

	getFixedSizeByDimensions(packageCode: string, placementCode: string, moduleCode: string, columns: number, height: number): Size | undefined {
		if (!this._isReady) {
			throw "The ProductPlacementSizeComboService is not initialized. Please call init() and subscribe for its Observable";
		}

		const sizes = this._sizesPerProductAndPlacement.get(this.createProductPlacementKey(packageCode, placementCode)) ?? [];
		for (let size of sizes) {
			if ((moduleCode && moduleCode === size.moduleCode) || columns === size.columns && height === size.height) {
				return size;
			}
		}
		return;
	}

	getPlacementSizesPerProductAndPlacement(packageCode: string, placementCode: string): Set<PlacementSize> | undefined {
		const productPlacementKey = this.createProductPlacementKey(packageCode, placementCode);
		return this._sizesPerProductAndPlacement.get(productPlacementKey);
	}

	private internalInit(validCombos: PackagePlacementsRelation[] | undefined) {
		if (validCombos && validCombos.length > 0) {
			for (let validCombo of validCombos) {
				if (validCombo.placements && validCombo.placements.length > 0) {
					for (let placement of validCombo.placements) {
						// add the product-placement combination into the validProductsPlacements

						const sizesPerProductAndPlacement = new Set<PlacementSize>();

						if (validCombo.packageCode && placement.placementCode) {
							const productPlacementKey = this.createProductPlacementKey(validCombo.packageCode, placement.placementCode);
							this._validProductsPlacements.add(productPlacementKey); // TODO: is it obsolete? Can we use the this._fullyQualifiedPlacements instead?
							this._fullyQualifiedPlacements.set(productPlacementKey, placement);

							this._sizesPerProductAndPlacement.set(productPlacementKey, sizesPerProductAndPlacement);
						}

						if (placement.placementCode && !this._placements.has(placement.placementCode)) {
							this._placements.set(placement.placementCode, placement);
						}

						if (placement.fixedSizes && placement.fixedSizes.length > 0) {
							for (let size of placement.fixedSizes) {
								// add the product-placement-size combination into the validProductsPlacementsSizes
								if (validCombo.packageCode && placement.placementCode && size.label) {
									const productPlacementSizeKey = this.createProductPlacementSizeKey(validCombo.packageCode, placement.placementCode, size.label);
									this._validProductsPlacementsSizes.add(productPlacementSizeKey);

									const placementSize = new PlacementSize(size);
									// This collection of all sizes is only used to show the common sizes when building order lines
									// It needs a label and height and width fractions to draw the size widget.
									// Skip those with 0 fractions (bad config)
									if (size.pageHeightFraction > 0 && size.pageWidthFraction > 0)  {
										this._placementSizes.set(size.label, placementSize);
									}

									this._fullyQualifiedPlacementSizes.set(productPlacementSizeKey, placementSize);

									// add the size to the product-placement combo
									sizesPerProductAndPlacement.add(placementSize);
								}

								// part II: _validProductsSizes
								if (validCombo.packageCode && size.label) {
									const productSizeKey = this.createProductSizeKey(validCombo.packageCode, size.label);
									this._validProductsSizes.add(productSizeKey);
								}

								// part III: _validPlacementsSizes
								if (placement.placementCode && size.label) {
									const placementSizeKey = this.createPlacementSizeKey(placement.placementCode, size.label);
									this._validPlacementsSizes.add(placementSizeKey);
								}
							}
						}
					}
				}
			}
		}
	}

	private createProductPlacementSizeKey(productId: string, placementId: string, sizeLabel: string): string {
		return `${productId}-${placementId}-${sizeLabel}-ppls`;
	}

	private createProductPlacementKey(productId: string, placementId: string): string {
		return `${productId}-${placementId}-ppl`;
	}

	private createProductSizeKey(productId: string, sizeLabel: string): string {
		return `${productId}-${sizeLabel}-ps`;
	}

	private createPlacementSizeKey(placementId: string, sizeLabel: string): string {
		return `${placementId}-${sizeLabel}-pls`;
	}

	//TODO: Remove this hack
	private getHackyModuleSize(moduleCode: string): { cols: number, height: number } {
		const moduleHeight = 372 / 8;
		let ret = { cols: 0, height: 0 };
		const matches = moduleCode.match(/(\d)(\d\d?)([bB])?/);
		if (matches) {
			ret.cols = parseInt(matches[1]);
			ret.height = parseInt(matches[2]) * moduleHeight;
			if (matches[3]) {
				// It's a B module - subtract half a module height
				ret.height -= moduleHeight / 2;
			}
		}
		// even more hacky - 5n modules are always whole-page width for T5 pages, so change them to 6n to show the size shape as full width!
		if (ret.cols === 5) ret.cols = 6;
		return ret;
	}
}
